/**
 * DetailsSidePanel Component
 *
 * A generic, reusable inline side panel with a sticky header area and close button.
 * Designed to sit alongside main content in a flex layout (not a modal or overlay).
 * Content-agnostic — consumers pass in their own header and body content as children.
 */

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { X } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx"

const PANEL_WIDTH_DEFAULT = "400px"

/** Props for the DetailsSidePanel component. */
interface DetailsSidePanelProps {
  /** Callback fired when the close button is clicked. */
  onClose: () => void
  /** Optional header content rendered in the sticky top bar (left of close button). */
  header?: React.ReactNode
  /** Optional actions rendered between the header and close button. */
  headerActions?: React.ReactNode
  /** The panel body content. */
  children: React.ReactNode
  /** Optional width class override. Defaults to "w-[400px]". */
  widthClass?: string
  /** Optional additional CSS classes applied to the outer container. */
  className?: string
}

/**
 * A generic inline side panel with a close button and scrollable content area.
 *
 * @param props - Component props.
 * @returns The rendered side panel element.
 */
export default function DetailsSidePanel({
  onClose,
  header,
  headerActions,
  children,
  widthClass = `w-[${PANEL_WIDTH_DEFAULT}]`,
  className = "",
}: DetailsSidePanelProps): React.JSX.Element {
  return (
    <div
      className={cn(
        "h-full shrink-0 border-l border-border bg-card flex flex-col",
        "animate-slide-in-from-right",
        widthClass,
        className
      )}
    >
      {/* Sticky header with close button */}
      <div className="shrink-0 border-b border-border p-4 flex items-center justify-between gap-2">
        {/* Header content slot */}
        <div className="min-w-0 flex-1">{header}</div>
        {/* Optional header actions */}
        {headerActions && <div className="shrink-0">{headerActions}</div>}
        <div className="flex flex-col justify-start h-full">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon-sm" onClick={onClose} className="shadow-none shrink-0">
                <X />
                <span className="sr-only">Close panel</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">Close details</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Body content — consumers control their own overflow behavior */}
      <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">{children}</div>
    </div>
  )
}
